import { clsx } from "clsx";
import { FileText, Mail, MessageSquareText, Send } from "lucide-react";
import type { ConsentChannel } from "@/types/clients";

/** Electronic channels plus "Paper": a signed form uploaded as a scan instead of sending anything. */
export type ChannelChoice = ConsentChannel | "Paper";

const OPTIONS: {
  value: ChannelChoice;
  label: string;
  hint: string;
  icon: React.ReactNode;
  needsEmail: boolean;
}[] = [
  {
    value: "Email",
    label: "E-mail",
    hint: "Zalecany, gdy klient ma e-mail.",
    icon: <Mail size={18} aria-hidden />,
    needsEmail: true,
  },
  {
    value: "Sms",
    label: "SMS",
    hint: "Gdy klient nie ma e-maila.",
    icon: <MessageSquareText size={18} aria-hidden />,
    needsEmail: false,
  },
  {
    value: "SmsAndEmail",
    label: "SMS i e-mail",
    hint: "Wyjątkowo — gdy wiadomość musi dotrzeć.",
    icon: <Send size={18} aria-hidden />,
    needsEmail: true,
  },
  {
    value: "Paper",
    label: "Zgoda papierowa",
    hint: "Klient podpisał formularz — dołączysz skan PDF.",
    icon: <FileText size={18} aria-hidden />,
    needsEmail: false,
  },
];

/** Default: free e-mail whenever the client has one; SMS otherwise. */
export function defaultChannel(hasEmail: boolean): ConsentChannel {
  return hasEmail ? "Email" : "Sms";
}

interface ChannelPickerProps {
  value: ChannelChoice | null;
  onChange: (value: ChannelChoice) => void;
  hasEmail: boolean;
  smsAvailable: boolean;
}

export function ChannelPicker({
  value,
  onChange,
  hasEmail,
  smsAvailable,
}: ChannelPickerProps) {
  return (
    <fieldset>
      <legend className="mb-2 text-sm font-medium text-main">
        Wyślij wniosek przez
      </legend>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {OPTIONS.map((o) => {
          const needsSms = o.value === "Sms" || o.value === "SmsAndEmail";
          const disabled =
            (o.needsEmail && !hasEmail) || (needsSms && !smsAvailable);
          const selected = value === o.value;
          return (
            <label
              key={o.value}
              className={clsx(
                "flex cursor-pointer flex-col gap-1 rounded-lg border px-3 py-2 text-sm transition-colors",
                selected
                  ? "border-adara-600 bg-adara-50 dark:bg-adara-900/30"
                  : "border-edge hover:bg-subtle",
                disabled && "cursor-not-allowed opacity-50",
              )}
            >
              <span className="flex items-center gap-2 font-medium text-main">
                <input
                  type="radio"
                  name="consent-channel"
                  className="accent-adara-700"
                  checked={selected}
                  disabled={disabled}
                  onChange={() => {
                    onChange(o.value);
                  }}
                />
                {o.icon}
                {o.label}
              </span>
              <span className="text-xs text-faint">
                {needsSms && !smsAvailable
                  ? "Wkrótce."
                  : disabled
                    ? "Wymaga e-maila klienta."
                    : o.hint}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
